import React, { useCallback, useEffect, useState } from 'react';
import { Users, Search, Plus, Edit2, Trash2, X, Filter, ChevronLeft, ChevronRight } from 'lucide-react';
import AuroraFloatingCard from '../ui/AuroraFloatingCard';
// Öğretmen veri tipi tanımı
interface Teacher {
  id: number;
  firstName: string;
  lastName: string;
  email: string;
  phone: string;
  branch: string;
  assignedClasses: string[];
  status: boolean;
}
// Branş seçenekleri
const branchOptions = ['Matematik', 'Türkçe', 'Fen Bilimleri', 'Sosyal Bilgiler', 'İngilizce', 'Din Kültürü', 'Beden Eğitimi', 'Görsel Sanatlar', 'Müzik', 'Bilişim Teknolojileri', 'Rehberlik', 'Fizik', 'Kimya', 'Biyoloji', 'Tarih', 'Coğrafya', 'Felsefe'];
// Sınıf seçenekleri
const classOptions = ['5-A', '5-B', '6-A', '6-B', '7-A', '7-B', '8-A', '8-B', '9-A', '9-B', '10-A', '10-B', '11-A', '11-B', '12-A', '12-B'];
// Örnek öğretmen verileri
const initialTeachers: Teacher[] = [{
  id: 1,
  firstName: 'Ayşe',
  lastName: 'Yılmaz',
  email: 'ayse.yilmaz@example.com',
  phone: '555-123-4567',
  branch: 'Matematik',
  assignedClasses: ['8-A', '8-B', '7-A'],
  status: true
}, {
  id: 2,
  firstName: 'Mehmet',
  lastName: 'Demir',
  email: 'mehmet.demir@example.com',
  phone: '555-234-5678',
  branch: 'Fen Bilimleri',
  assignedClasses: ['7-A', '7-B', '8-A'],
  status: true
}
// Daha fazla öğretmen eklenebilir
];
export function OgretmenYonetimi() {
  // State tanımlamaları
  const [teachers, setTeachers] = useState<Teacher[]>(initialTeachers);
  const [filteredTeachers, setFilteredTeachers] = useState<Teacher[]>(teachers);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedBranch, setSelectedBranch] = useState<string>('');
  const [showAddModal, setShowAddModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [currentTeacher, setCurrentTeacher] = useState<Teacher | null>(null);
  const [isEditMode, setIsEditMode] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const teachersPerPage = 5;
  // Form state'i
  const [formData, setFormData] = useState({
    firstName: '',
    lastName: '',
    email: '',
    phone: '',
    branch: branchOptions[0],
    assignedClasses: [] as string[]
  });
  // Form validasyon hataları
  const [formErrors, setFormErrors] = useState({
    firstName: '',
    email: ''
  });
  // Öğretmen arama ve filtreleme
  useEffect(() => {
    let result = teachers;
    // İsme göre arama
    if (searchTerm) {
      result = result.filter(teacher => `${teacher.firstName} ${teacher.lastName}`.toLowerCase().includes(searchTerm.toLowerCase()) || teacher.email.toLowerCase().includes(searchTerm.toLowerCase()));
    }
    // Branşa göre filtreleme
    if (selectedBranch) {
      result = result.filter(teacher => teacher.branch === selectedBranch);
    }
    setFilteredTeachers(result);
    setCurrentPage(1);
  }, [searchTerm, selectedBranch, teachers]);
  // Pagination hesaplamaları
  const indexOfLastTeacher = currentPage * teachersPerPage;
  const indexOfFirstTeacher = indexOfLastTeacher - teachersPerPage;
  const currentTeachers = filteredTeachers.slice(indexOfFirstTeacher, indexOfLastTeacher);
  const totalPages = Math.ceil(filteredTeachers.length / teachersPerPage);
  // Form değişikliklerini işleme
  const handleInputChange = useCallback((e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
    const {
      name,
      value
    } = e.target;
    setFormData(prevData => ({
      ...prevData,
      [name]: value
    }));
    // Validasyon
    if (name === 'firstName') {
      if (!value.trim()) {
        setFormErrors(prev => ({
          ...prev,
          firstName: 'Ad alanı zorunludur'
        }));
      } else {
        setFormErrors(prev => ({
          ...prev,
          firstName: ''
        }));
      }
    }
    if (name === 'email') {
      const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
      if (!value.trim()) {
        setFormErrors(prev => ({
          ...prev,
          email: 'E-posta alanı zorunludur'
        }));
      } else if (!emailRegex.test(value)) {
        setFormErrors(prev => ({
          ...prev,
          email: 'Geçerli bir e-posta adresi giriniz'
        }));
      } else {
        setFormErrors(prev => ({
          ...prev,
          email: ''
        }));
      }
    }
  }, []);
  // Ekleme/düzenleme modalını kapat
  const closeAddModal = useCallback(() => {
    setShowAddModal(false);
    setIsEditMode(false);
    setCurrentTeacher(null);
    setFormData({
      firstName: '',
      lastName: '',
      email: '',
      phone: '',
      branch: branchOptions[0],
      assignedClasses: []
    });
    setFormErrors({
      firstName: '',
      email: ''
    });
  }, []);
  // Öğretmen ekleme veya güncelleme
  const handleSaveTeacher = useCallback(() => {
    // Validasyon
    let hasError = false;
    const newErrors = {
      ...formErrors
    };
    if (!formData.firstName.trim()) {
      newErrors.firstName = 'Ad alanı zorunludur';
      hasError = true;
    }
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!formData.email.trim()) {
      newErrors.email = 'E-posta alanı zorunludur';
      hasError = true;
    } else if (!emailRegex.test(formData.email)) {
      newErrors.email = 'Geçerli bir e-posta adresi giriniz';
      hasError = true;
    }
    setFormErrors(newErrors);
    if (hasError) return;
    if (isEditMode && currentTeacher) {
      // Öğretmeni güncelle
      const updatedTeachers = teachers.map(teacher => teacher.id === currentTeacher.id ? {
        ...teacher,
        firstName: formData.firstName,
        lastName: formData.lastName,
        email: formData.email,
        phone: formData.phone,
        branch: formData.branch,
        assignedClasses: formData.assignedClasses
      } : teacher);
      setTeachers(updatedTeachers);
    } else {
      // Yeni öğretmen ekle
      const newTeacher: Teacher = {
        id: Math.max(...teachers.map(t => t.id), 0) + 1,
        firstName: formData.firstName,
        lastName: formData.lastName,
        email: formData.email,
        phone: formData.phone,
        branch: formData.branch,
        assignedClasses: formData.assignedClasses,
        status: true
      };
      setTeachers([...teachers, newTeacher]);
    }
    closeAddModal();
  }, [formData, formErrors, isEditMode, currentTeacher, teachers, closeAddModal]);
  // Sınıf seçme işlemi
  const handleClassSelection = (className: string) => {
    setFormData(prevData => {
      if (prevData.assignedClasses.includes(className)) {
        return {
          ...prevData,
          assignedClasses: prevData.assignedClasses.filter(c => c !== className)
        };
      } else {
        return {
          ...prevData,
          assignedClasses: [...prevData.assignedClasses, className]
        };
      }
    });
  };
  // Öğretmen silme
  const handleDeleteTeacher = () => {
    if (currentTeacher) {
      const updatedTeachers = teachers.map(teacher => teacher.id === currentTeacher.id ? {
        ...teacher,
        status: false
      } : teacher);
      setTeachers(updatedTeachers);
      setShowDeleteModal(false);
      setCurrentTeacher(null);
    }
  };
  // Düzenleme modalını aç
  const openEditModal = (teacher: Teacher) => {
    setCurrentTeacher(teacher);
    setFormData({
      firstName: teacher.firstName,
      lastName: teacher.lastName,
      email: teacher.email,
      phone: teacher.phone,
      branch: teacher.branch,
      assignedClasses: [...teacher.assignedClasses]
    });
    setIsEditMode(true);
    setShowAddModal(true);
  };
  // Silme modalını aç
  const openDeleteModal = (teacher: Teacher) => {
    setCurrentTeacher(teacher);
    setShowDeleteModal(true);
  };
  // Pagination işlevleri
  const goToPage = (page: number) => {
    if (page >= 1 && page <= totalPages) {
      setCurrentPage(page);
    }
  };
  // Öğretmen Silme Onay Modalı
  const DeleteConfirmationModal = () => <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 backdrop-blur-sm" data-id="element-981">
      <AuroraFloatingCard className="w-full max-w-md" data-id="element-982">
        <div className="p-6" data-id="element-983">
          <h3 className="text-xl font-bold text-white mb-4" data-id="element-984">
            Öğretmeni Pasif Yap
          </h3>
          <p className="text-gray-300 mb-6" data-id="element-985">
            <span className="font-semibold text-purple-400" data-id="element-986">
              {currentTeacher?.firstName} {currentTeacher?.lastName}
            </span>{' '}
            isimli öğretmeni pasif duruma getirmek istediğinize emin misiniz?
          </p>
          <div className="flex justify-end gap-3" data-id="element-987">
            <button onClick={() => setShowDeleteModal(false)} className="px-4 py-2 bg-gray-700 hover:bg-gray-600 text-white rounded-lg transition-colors" data-id="element-988">
              İptal
            </button>
            <button onClick={handleDeleteTeacher} className="px-4 py-2 bg-gradient-to-r from-red-500 to-pink-600 hover:opacity-90 text-white rounded-lg transition-colors" data-id="element-989">
              Pasif Yap
            </button>
          </div>
        </div>
      </AuroraFloatingCard>
    </div>;
  return <div className="space-y-6" data-id="element-990">
      {/* Başlık */}
      <div className="flex items-center gap-3" data-id="element-991">
        <h1 className="text-3xl font-bold" data-id="element-992">Öğretmen Yönetimi</h1>
        <Users className="w-8 h-8 text-purple-400" data-id="element-993" />
      </div>
      {/* Ana Kart */}
      <AuroraFloatingCard data-id="element-994">
        {/* Üst Kontroller */}
        <div className="flex flex-col md:flex-row justify-between items-start md:items-center mb-6 gap-4" data-id="element-995">
          <div className="flex flex-col sm:flex-row gap-3 w-full md:w-auto" data-id="element-996">
            {/* Arama */}
            <div className="relative" data-id="element-997">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" size={18} data-id="element-998" />
              <input type="text" placeholder="Öğretmen adı veya e-posta ile ara..." value={searchTerm} onChange={e => setSearchTerm(e.target.value)} className="pl-10 pr-4 py-2 w-full sm:w-64 bg-gray-800/50 border border-purple-500/30 rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-transparent" data-id="element-999" />
            </div>
            {/* Branş Filtresi */}
            <div className="relative" data-id="element-1000">
              <Filter className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" size={18} data-id="element-1001" />
              <select value={selectedBranch} onChange={e => setSelectedBranch(e.target.value)} className="pl-10 pr-10 py-2 w-full sm:w-48 bg-gray-800/50 border border-purple-500/30 rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-transparent appearance-none" style={{
              backgroundImage: `linear-gradient(rgba(30, 30, 40, 0.4), rgba(20, 20, 30, 0.4))`,
              boxShadow: 'inset 0 1px 1px rgba(255, 255, 255, 0.05)'
            }} data-id="element-1002">
                <option value="" className="bg-gray-800 text-white" data-id="element-1003">
                  Tüm Branşlar
                </option>
                {branchOptions.map(option => <option key={option} value={option} className="bg-gray-800 text-white" data-id="element-1004">
                    {option}
                  </option>)}
              </select>
              <div className="absolute inset-y-0 right-0 flex items-center px-2 pointer-events-none" data-id="element-1005">
                <svg className="w-5 h-5 text-purple-400" fill="none" viewBox="0 0 24 24" stroke="currentColor" data-id="element-1006">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" data-id="element-1007" />
                </svg>
              </div>
            </div>
          </div>
          {/* Yeni Öğretmen Ekle Butonu */}
          <button onClick={() => {
          setIsEditMode(false);
          setShowAddModal(true);
        }} className="px-4 py-2 bg-gradient-to-r from-purple-500 to-blue-500 hover:opacity-90 text-white rounded-lg transition-colors flex items-center gap-2 whitespace-nowrap" data-id="element-1008">
            <Plus size={18} data-id="element-1009" />
            Yeni Öğretmen Ekle
          </button>
        </div>
        {/* Öğretmen Tablosu veya Boş Durum */}
        {teachers.length === 0 ? <div className="text-center py-12" data-id="element-1010">
            <Users className="w-16 h-16 text-gray-600 mx-auto mb-4" data-id="element-1011" />
            <p className="text-gray-400 mb-4" data-id="element-1012">
              Henüz hiç öğretmen kaydı bulunmuyor. İlk öğretmeni eklemek için '+
              Yeni Öğretmen Ekle' butonuna tıklayın.
            </p>
          </div> : <>
            {/* Öğretmen Tablosu */}
            <div className="overflow-x-auto" data-id="element-1013">
              <table className="w-full" data-id="element-1014">
                <thead data-id="element-1015">
                  <tr className="border-b border-gray-700" data-id="element-1016">
                    <th className="px-4 py-3 text-left text-sm font-medium text-gray-300" data-id="element-1017">
                      Adı Soyadı
                    </th>
                    <th className="px-4 py-3 text-left text-sm font-medium text-gray-300" data-id="element-1018">
                      Branşı
                    </th>
                    <th className="px-4 py-3 text-left text-sm font-medium text-gray-300" data-id="element-1019">
                      E-posta
                    </th>
                    <th className="px-4 py-3 text-left text-sm font-medium text-gray-300" data-id="element-1020">
                      Telefon
                    </th>
                    <th className="px-4 py-3 text-left text-sm font-medium text-gray-300" data-id="element-1021">
                      Sınıflar
                    </th>
                    <th className="px-4 py-3 text-left text-sm font-medium text-gray-300" data-id="element-1022">
                      Durum
                    </th>
                    <th className="px-4 py-3 text-right text-sm font-medium text-gray-300" data-id="element-1023">
                      Eylemler
                    </th>
                  </tr>
                </thead>
                <tbody data-id="element-1024">
                  {currentTeachers.map(teacher => <tr key={teacher.id} className={`border-b border-gray-800 ${!teacher.status ? 'opacity-50' : ''}`} data-id="element-1025">
                      <td className="px-4 py-3 text-white" data-id="element-1026">
                        {teacher.firstName} {teacher.lastName}
                      </td>
                      <td className="px-4 py-3 text-gray-300" data-id="element-1027">
                        {teacher.branch}
                      </td>
                      <td className="px-4 py-3 text-gray-300" data-id="element-1028">
                        {teacher.email}
                      </td>
                      <td className="px-4 py-3 text-gray-300" data-id="element-1029">
                        {teacher.phone}
                      </td>
                      <td className="px-4 py-3" data-id="element-1030">
                        <div className="flex flex-wrap gap-1" data-id="element-1031">
                          {teacher.assignedClasses.map(cls => <span key={cls} className="inline-block px-2 py-1 text-xs bg-purple-500/10 text-purple-400 rounded" data-id="element-1032">
                              {cls}
                            </span>)}
                        </div>
                      </td>
                      <td className="px-4 py-3" data-id="element-1033">
                        <span className={`inline-flex items-center px-2.5 py-1 rounded-full text-xs font-medium ${teacher.status ? 'bg-green-500/10 text-green-400' : 'bg-red-500/10 text-red-400'}`} data-id="element-1034">
                          {teacher.status ? 'Aktif' : 'Pasif'}
                        </span>
                      </td>
                      <td className="px-4 py-3 text-right" data-id="element-1035">
                        <div className="flex justify-end gap-2" data-id="element-1036">
                          <button onClick={() => openEditModal(teacher)} className="p-1.5 bg-blue-500/10 hover:bg-blue-500/20 text-blue-400 rounded-lg transition-colors" title="Düzenle" data-id="element-1037">
                            <Edit2 size={16} data-id="element-1038" />
                          </button>
                          {teacher.status && <button onClick={() => openDeleteModal(teacher)} className="p-1.5 bg-red-500/10 hover:bg-red-500/20 text-red-400 rounded-lg transition-colors" title="Pasif Yap" data-id="element-1039">
                              <Trash2 size={16} data-id="element-1040" />
                            </button>}
                        </div>
                      </td>
                    </tr>)}
                </tbody>
              </table>
            </div>
            {/* Pagination */}
            {totalPages > 1 && <div className="flex justify-center items-center gap-2 mt-6" data-id="element-1041">
                <button onClick={() => goToPage(currentPage - 1)} disabled={currentPage === 1} className="p-1.5 rounded-lg bg-gray-700/50 text-gray-300 hover:bg-gray-700 disabled:opacity-50 disabled:cursor-not-allowed" data-id="element-1042">
                  <ChevronLeft size={18} data-id="element-1043" />
                </button>
                {Array.from({
            length: totalPages
          }, (_, i) => i + 1).map(page => <button key={page} onClick={() => goToPage(page)} className={`w-8 h-8 rounded-lg ${currentPage === page ? 'bg-gradient-to-r from-purple-600 to-blue-600 text-white' : 'bg-gray-700/50 text-gray-300 hover:bg-gray-700'}`} data-id="element-1044">
                    {page}
                  </button>)}
                <button onClick={() => goToPage(currentPage + 1)} disabled={currentPage === totalPages} className="p-1.5 rounded-lg bg-gray-700/50 text-gray-300 hover:bg-gray-700 disabled:opacity-50 disabled:cursor-not-allowed" data-id="element-1045">
                  <ChevronRight size={18} data-id="element-1046" />
                </button>
              </div>}
          </>}
      </AuroraFloatingCard>
      {/* Modaller */}
      {showAddModal && <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 backdrop-blur-sm" data-id="element-1047">
          <AuroraFloatingCard className="w-full max-w-2xl" data-id="element-1048">
            <div className="p-6" data-id="element-1049">
              <div className="flex justify-between items-center mb-6" data-id="element-1050">
                <h3 className="text-xl font-bold text-white" data-id="element-1051">
                  {isEditMode ? 'Öğretmen Düzenle' : 'Yeni Öğretmen Ekle'}
                </h3>
                <button onClick={closeAddModal} className="text-gray-400 hover:text-white transition-colors" type="button" data-id="element-1052">
                  <X size={24} data-id="element-1053" />
                </button>
              </div>
              <div className="space-y-4" data-id="element-1054">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4" data-id="element-1055">
                  {/* Ad */}
                  <div data-id="element-1056">
                    <label htmlFor="firstName" className="block text-sm font-medium text-gray-300 mb-1" data-id="element-1057">
                      Adı <span className="text-red-500" data-id="element-1058">*</span>
                    </label>
                    <input type="text" id="firstName" name="firstName" value={formData.firstName} onChange={handleInputChange} className={`w-full bg-gray-800/50 border ${formErrors.firstName ? 'border-red-500' : 'border-purple-500/30'} rounded-lg px-3 py-2 text-white focus:outline-none focus:ring-2 focus:ring-purple-500`} data-id="element-1059" />
                    {formErrors.firstName && <p className="mt-1 text-sm text-red-500" data-id="element-1060">
                        {formErrors.firstName}
                      </p>}
                  </div>
                  {/* Soyad */}
                  <div data-id="element-1061">
                    <label htmlFor="lastName" className="block text-sm font-medium text-gray-300 mb-1" data-id="element-1062">
                      Soyadı
                    </label>
                    <input type="text" id="lastName" name="lastName" value={formData.lastName} onChange={handleInputChange} className="w-full bg-gray-800/50 border border-purple-500/30 rounded-lg px-3 py-2 text-white focus:outline-none focus:ring-2 focus:ring-purple-500" data-id="element-1063" />
                  </div>
                  {/* E-posta */}
                  <div data-id="element-1064">
                    <label htmlFor="email" className="block text-sm font-medium text-gray-300 mb-1" data-id="element-1065">
                      E-posta <span className="text-red-500" data-id="element-1066">*</span>
                    </label>
                    <input type="email" id="email" name="email" value={formData.email} onChange={handleInputChange} className={`w-full bg-gray-800/50 border ${formErrors.email ? 'border-red-500' : 'border-purple-500/30'} rounded-lg px-3 py-2 text-white focus:outline-none focus:ring-2 focus:ring-purple-500`} data-id="element-1067" />
                    {formErrors.email && <p className="mt-1 text-sm text-red-500" data-id="element-1068">
                        {formErrors.email}
                      </p>}
                  </div>
                  {/* Telefon */}
                  <div data-id="element-1069">
                    <label htmlFor="phone" className="block text-sm font-medium text-gray-300 mb-1" data-id="element-1070">
                      Telefon
                    </label>
                    <input type="text" id="phone" name="phone" value={formData.phone} onChange={handleInputChange} className="w-full bg-gray-800/50 border border-purple-500/30 rounded-lg px-3 py-2 text-white focus:outline-none focus:ring-2 focus:ring-purple-500" data-id="element-1071" />
                  </div>
                  {/* Branş */}
                  <div className="md:col-span-2" data-id="element-1072">
                    <label htmlFor="branch" className="block text-sm font-medium text-gray-300 mb-1" data-id="element-1073">
                      Branş
                    </label>
                    <select id="branch" name="branch" value={formData.branch} onChange={handleInputChange} className="w-full bg-gray-800/50 border border-purple-500/30 rounded-lg px-3 py-2 text-white focus:outline-none focus:ring-2 focus:ring-purple-500" data-id="element-1074">
                      {branchOptions.map(option => <option key={option} value={option} data-id="element-1075">
                          {option}
                        </option>)}
                    </select>
                  </div>
                  {/* Sınıf Seçimi */}
                  <div className="md:col-span-2" data-id="element-1076">
                    <label className="block text-sm font-medium text-gray-300 mb-2" data-id="element-1077">
                      Atanacağı Sınıflar
                    </label>
                    <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-2" data-id="element-1078">
                      {classOptions.map(className => <div key={className} onClick={() => handleClassSelection(className)} className={`p-2 rounded-lg cursor-pointer text-center text-sm transition-colors ${formData.assignedClasses.includes(className) ? 'bg-purple-500/30 border border-purple-500 text-white' : 'bg-gray-800/50 border border-gray-700 text-gray-400 hover:bg-gray-700/50'}`} data-id="element-1079">
                          {className}
                        </div>)}
                    </div>
                    <p className="mt-2 text-xs text-gray-500" data-id="element-1080">
                      Öğretmenin ders vereceği sınıfları seçin
                    </p>
                  </div>
                </div>
              </div>
              <div className="flex justify-end gap-3 mt-6" data-id="element-1081">
                <button onClick={closeAddModal} className="px-4 py-2 bg-gray-700 hover:bg-gray-600 text-white rounded-lg transition-colors" type="button" data-id="element-1082">
                  İptal
                </button>
                <button onClick={handleSaveTeacher} className="px-4 py-2 bg-gradient-to-r from-purple-600 to-indigo-600 hover:opacity-90 text-white rounded-lg transition-opacity" type="button" data-id="element-1083">
                  {isEditMode ? 'Güncelle' : 'Ekle'}
                </button>
              </div>
            </div>
          </AuroraFloatingCard>
        </div>}
      {showDeleteModal && <DeleteConfirmationModal data-id="element-1084" />}
    </div>;
}